import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { ArrowLeft, Shield, Building2, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { apiErrorMessage } from '../../lib/utils';
import { logger } from '../../lib/logger';

const RolesDepartments = () => {
  const navigate = useNavigate();
  const [roles, setRoles] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addRoleModal, setAddRoleModal] = useState(false);
  const [addDeptModal, setAddDeptModal] = useState(false);
  const [newRole, setNewRole] = useState({ id: '', name: '', description: '' });
  const [newDept, setNewDept] = useState({ name: '' });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [rolesRes, deptsRes] = await Promise.all([
        api.get('/platform/roles'),
        api.get('/platform/departments')
      ]);
      setRoles(rolesRes.data.roles || []);
      setDepartments(deptsRes.data.departments || []);
    } catch (error) {
      logger.error('Error loading roles/departments:', error);
      toast.error(apiErrorMessage(error, 'Failed to load data. Please try again.'));
      setRoles([]);
      setDepartments([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAddRole = async () => {
    if (!newRole.id || !newRole.name) {
      toast.error('Role ID and name are required');
      return;
    }

    try {
      await api.post('/platform/roles', newRole);
      toast.success(`Role "${newRole.name}" created successfully`);
      setAddRoleModal(false);
      setNewRole({ id: '', name: '', description: '' });
      fetchData();
    } catch (error) {
      toast.error(apiErrorMessage(error, 'Failed to create role'));
    }
  };

  const handleDeleteRole = async (roleId, roleName) => {
    if (!confirm(`Delete role "${roleName}"? This action cannot be undone.`)) {
      return;
    }

    try {
      await api.delete(`/platform/roles/${roleId}`);
      toast.success('Role deleted successfully');
      fetchData();
    } catch (error) {
      toast.error(apiErrorMessage(error, 'Failed to delete role'));
    }
  };

  const handleAddDepartment = async () => {
    if (!newDept.name) {
      toast.error('Department name is required');
      return;
    }

    try {
      await api.post('/platform/departments', newDept);
      toast.success(`Department "${newDept.name}" created successfully`);
      setAddDeptModal(false);
      setNewDept({ name: '' });
      fetchData();
    } catch (error) {
      toast.error(apiErrorMessage(error, 'Failed to create department'));
    }
  };

  const handleDeleteDepartment = async (deptId, deptName) => {
    if (!confirm(`Delete department "${deptName}"? This action cannot be undone.`)) {
      return;
    }

    try {
      await api.delete(`/platform/departments/${deptId}`);
      toast.success('Department deleted successfully');
      fetchData();
    } catch (error) {
      toast.error(apiErrorMessage(error, 'Failed to delete department'));
    }
  };

  if (loading) {
    return <div className="p-8 text-center">Loading...</div>;
  }

  return (
    <div data-testid="roles-departments-page">
      <header className="page-header glass-header">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/platform')}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-slate-900 font-[Manrope]">Roles & Departments</h1>
            <p className="text-slate-500">Manage system-wide roles and departments</p>
          </div>
        </div>
      </header>

      <div className="page-content max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Roles Management */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Shield className="w-5 h-5 text-purple-600" />
                  User Roles
                </CardTitle>
                <Button
                  size="sm"
                  onClick={() => setAddRoleModal(true)}
                  className="bg-purple-500 hover:bg-purple-600"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Role
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {roles.map((role) => (
                  <div
                    key={role.id}
                    className="flex items-center justify-between p-3 border border-slate-200 rounded-lg hover:bg-slate-50"
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <p className="font-medium text-slate-900">{role.name}</p>
                        {role.is_system && (
                          <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                            System
                          </Badge>
                        )}
                      </div>
                      {role.description && (
                        <p className="text-sm text-slate-500 mt-1">{role.description}</p>
                      )}
                    </div>
                    {!role.is_system && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleDeleteRole(role.id, role.name)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
              <p className="text-xs text-slate-500 mt-4">
                💡 System roles cannot be deleted. Custom roles can be deleted if no users have them.
              </p>
            </CardContent>
          </Card>

          {/* Departments Management */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Building2 className="w-5 h-5 text-blue-600" />
                  Departments
                </CardTitle>
                <Button
                  size="sm"
                  onClick={() => setAddDeptModal(true)}
                  className="bg-blue-500 hover:bg-blue-600"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Department
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {departments.map((dept) => (
                  <div
                    key={dept.id}
                    className="flex items-center justify-between p-3 border border-slate-200 rounded-lg hover:bg-slate-50"
                  >
                    <div className="flex items-center gap-2">
                      <p className="font-medium text-slate-900">{dept.name}</p>
                      {dept.is_system && (
                        <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                          System
                        </Badge>
                      )}
                    </div>
                    {!dept.is_system && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleDeleteDepartment(dept.id, dept.name)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
              <p className="text-xs text-slate-500 mt-4">
                💡 System departments cannot be deleted. Custom departments can be deleted if no users are assigned.
              </p>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Add Role Modal */}
      <Dialog open={addRoleModal} onOpenChange={setAddRoleModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add New Role</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label>Role ID * (lowercase, no spaces)</Label>
              <Input
                value={newRole.id}
                onChange={(e) => setNewRole({...newRole, id: e.target.value.toLowerCase().replace(/\s+/g, '_')})}
                placeholder="e.g., physiotherapist"
              />
              <p className="text-xs text-slate-500 mt-1">This will be used internally in the system</p>
            </div>
            <div>
              <Label>Display Name *</Label>
              <Input
                value={newRole.name}
                onChange={(e) => setNewRole({...newRole, name: e.target.value})}
                placeholder="e.g., Physiotherapist"
              />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea
                value={newRole.description}
                onChange={(e) => setNewRole({...newRole, description: e.target.value})}
                placeholder="Brief description of this role"
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddRoleModal(false)}>Cancel</Button>
            <Button onClick={handleAddRole} className="bg-purple-500 hover:bg-purple-600">
              Create Role
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Department Modal */}
      <Dialog open={addDeptModal} onOpenChange={setAddDeptModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add New Department</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label>Department Name *</Label>
              <Input
                value={newDept.name}
                onChange={(e) => setNewDept({...newDept, name: e.target.value})}
                placeholder="e.g., Dermatology"
              />
            </div>
            <p className="text-sm text-slate-600">
              This department will be available to all institutions when creating users.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddDeptModal(false)}>Cancel</Button>
            <Button onClick={handleAddDepartment} className="bg-blue-500 hover:bg-blue-600">
              Create Department
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RolesDepartments;
